<template>
    <div class="pending-fields-panel">
        <div class="panel-header">
            <h4>
                <i class="fa fa-clipboard-list"></i>
                Pending Fields
            </h4>
            <span class="count-badge">{{ totalPending }}</span>
        </div>

        <div v-if="totalPending === 0" class="empty-state">
            <i class="fa fa-check-circle"></i>
            <p>All fields completed!</p>
        </div>

        <div v-else class="signatures-content">
            <!-- Group by Signee Type -->
            <div
                v-for="(fields, signeeType) in groupedFields"
                :key="signeeType"
                class="signature-group"
            >
                <h5 class="group-title">
                    <i class="fa fa-user"></i>
                    {{ formatSigneeType(signeeType) }} Fields
                </h5>
                <div class="signature-list">
                    <div
                        v-for="field in fields"
                        :key="field.id"
                        class="signature-item"
                        @click="$emit('field-click', field)"
                    >
                        <div class="signature-info">
                            <span
                                class="signee-badge"
                                :class="`badge-${signeeType}`"
                            >
                                {{ formatSigneeType(signeeType) }}
                            </span>
                            <span
                                class="field-type-badge"
                                :class="`type-${getFieldType(field)}`"
                            >
                                {{ formatFieldType(getFieldType(field)) }}
                            </span>
                            <span class="signature-label">
                                {{ field.field_label || field.label || field.field_name || field.name || 'Field' }}
                            </span>
                        </div>
                        <i class="fa fa-chevron-right"></i>
                    </div>
                </div>
            </div>

            <!-- Fallback: show all pending fields as a simple list -->
            <div v-if="Object.keys(groupedFields).length === 0 && pendingFields.length > 0" class="signature-group">
                <h5 class="group-title">
                    <i class="fa fa-clipboard-list"></i>
                    Required Fields
                </h5>
                <div class="signature-list">
                    <div
                        v-for="field in pendingFields"
                        :key="field.id"
                        class="signature-item"
                        @click="$emit('field-click', field)"
                    >
                        <div class="signature-info">
                            <span
                                class="field-type-badge"
                                :class="`type-${getFieldType(field)}`"
                            >
                                {{ formatFieldType(getFieldType(field)) }}
                            </span>
                            <span class="signature-label">
                                {{ field.field_label || field.label || field.field_name || field.name || 'Field Required' }}
                            </span>
                        </div>
                        <i class="fa fa-chevron-right"></i>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PendingFieldsPanel',
    props: {
        pendingFields: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        totalPending() {
            return this.pendingFields.length;
        },

        groupedFields() {
            // Group all fields by signee_type for cleaner display
            const groups = {};

            this.pendingFields.forEach(field => {
                // Get signee_type from field's annotation, current assignment,
                // or fall back to the field's authored assignable_to before
                // marking it as unassigned.
                const annotation = field.annotations?.find(a => a.signee_type);
                const signeeType = annotation?.signee_type
                    || field.assigned_to?.role
                    || field.assignable_to?.[0]
                    || 'unassigned';

                if (!groups[signeeType]) {
                    groups[signeeType] = [];
                }
                groups[signeeType].push(field);
            });

            return groups;
        }
    },
    methods: {
        formatSigneeType(type) {
            if (!type || type === 'null' || type === 'undefined' || type === 'unassigned') {
                return 'Unassigned';
            }
            const labels = {
                student: 'Student',
                guardian: 'Guardian',
                instructor: 'Instructor',
                staff: 'Staff',
                admin: 'Admin'
            };
            return labels[type] || type;
        },

        getFieldType(field) {
            // Get field type from annotations or field_type property
            const annotation = field.annotations?.[0];
            return annotation?.type || field.field_type || 'text';
        },

        formatFieldType(type) {
            const labels = {
                signature: 'Signature',
                text: 'Text',
                textarea: 'Text Area',
                date: 'Date',
                email: 'Email',
                phone: 'Phone',
                number: 'Number',
                select: 'Select',
                dropdown: 'Dropdown',
                checkbox: 'Checkbox',
                radio: 'Radio'
            };
            return labels[type] || type;
        }
    }
};
</script>

<style scoped>
.pending-fields-panel {
    background-color: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
}

.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background-color: #f7fafc;
    border-bottom: 1px solid #e2e8f0;
}

.panel-header h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
    gap: 8px;
}

.panel-header h4 i {
    color: #4a5568;
}

.count-badge {
    background-color: #4299e1;
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 12px;
    min-width: 24px;
    text-align: center;
}

.empty-state {
    padding: 40px 20px;
    text-align: center;
    color: #48bb78;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.8;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    color: #2d3748;
}

.signatures-content {
    padding: 0;
}

.signature-group {
    border-bottom: 1px solid #e2e8f0;
}

.signature-group:last-child {
    border-bottom: none;
}

.group-title {
    margin: 0;
    padding: 12px 20px;
    font-size: 13px;
    font-weight: 600;
    color: #4a5568;
    background-color: #f7fafc;
    display: flex;
    align-items: center;
    gap: 8px;
}

.group-title i {
    font-size: 12px;
    color: #718096;
}

.signature-list {
    padding: 0;
}

.signature-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 20px;
    cursor: pointer;
    transition: all 0.2s;
    border-bottom: 1px solid #f7fafc;
}

.signature-item:hover {
    background-color: #f7fafc;
}

.signature-item:last-child {
    border-bottom: none;
}

.signature-info {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
}

.signee-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.badge-student {
    background-color: #3b82f6;
}

.badge-guardian {
    background-color: #10b981;
}

.badge-instructor {
    background-color: #f59e0b;
}

.badge-staff {
    background-color: #8b5cf6;
}

.badge-admin {
    background-color: #ef4444;
}

.badge-null,
.badge-undefined,
.badge-unassigned {
    background-color: #6b7280;
}

/* Field Type Badges */
.field-type-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.type-signature {
    background-color: #fef3c7;
    color: #92400e;
}

.type-text,
.type-textarea {
    background-color: #e0e7ff;
    color: #3730a3;
}

.type-date {
    background-color: #fce7f3;
    color: #9d174d;
}

.type-email {
    background-color: #cffafe;
    color: #0e7490;
}

.type-phone {
    background-color: #d1fae5;
    color: #065f46;
}

.type-number {
    background-color: #fef9c3;
    color: #854d0e;
}

.type-select,
.type-dropdown {
    background-color: #ede9fe;
    color: #5b21b6;
}

.type-checkbox,
.type-radio {
    background-color: #f3e8ff;
    color: #7c3aed;
}

.signature-label {
    font-size: 14px;
    color: #2d3748;
    font-weight: 500;
}

.signature-item > i {
    color: #cbd5e0;
    font-size: 12px;
}

/* Mobile Optimization */
@media (max-width: 768px) {
    .panel-header {
        padding: 12px 16px;
    }

    .panel-header h4 {
        font-size: 15px;
    }

    .signature-item {
        padding: 12px 16px;
        min-height: 44px;
    }

    .signature-label {
        font-size: 13px;
    }

    .signee-badge {
        font-size: 10px;
        padding: 3px 8px;
    }
}
</style>
